import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { fetchQuery } from "relay-runtime"
import type { useCareProviderQuickListQuery } from "@/modules/care-provider/simple-search/services/__generated__/useCareProviderQuickListQuery.graphql"
import { careProviderQuickListQuery } from "@/modules/care-provider/simple-search/services/useCareProviderQuickListQuery"
import { createCareProviderQuickListQueryVariables } from "@/modules/care-provider/utils/create-care-provider-quick-list-query-variables"
import { canBetterDocUsageStillChange } from "@/modules/patients/detail/better-doc/lib/can-better-doc-usage-still-change"
import {
  type BetterDocUsageEvaluation,
  fetchBetterDocUsageForConsultation,
} from "@/modules/patients/detail/better-doc/lib/fetch-better-doc-usage-for-consultation"
import { getRecordedDiagnosisCode } from "@/modules/patients/detail/better-doc/lib/get-recorded-diagnosis-code"
import { isBetterDocAvailable } from "@/modules/patients/detail/better-doc/lib/is-better-doc-available"
import { isBetterDocRequired } from "@/modules/patients/detail/better-doc/lib/is-better-doc-required"
import {
  ACTUAL_POINT_OF_CARE_FIELD,
  MEDICAL_POINT_OF_CARE_FIELD,
  SHOULD_VALIDATE_AND_DIRTY,
} from "@/modules/patients/detail/progress-note/point-of-care/constants"
import { useAppointmentOrRegisteredPatientAddress } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useAppointmentOrRegisteredPatientAddress"
import { isCareProviderPreselectable } from "@/modules/patients/detail/progress-note/point-of-care/lib/is-care-provider-preselectable"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { MedgateServiceInfo } from "@/modules/patients/services/usePatientByIdQuery"
import { RelayEnvironment } from "@/relay-environment"
import { CareProviderSearchType, Country, PointOfCareType } from "@/types/__generated__/union-types"

type UseCareProviderPreselectionHandlersParameters = {
  form: UseFormReturn<PointOfCareFormType>
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  patientId: string
  countryCode: Country
  medgateServiceInfo: MedgateServiceInfo
  diagnosisCode: string
}

type CareProviderPreselectionRequest = {
  searchType: CareProviderSearchType
  effectivePointOfCare: PointOfCareType
  specialtyId: string | null
  isChoiceStillCurrent: () => boolean
}

export const useCareProviderPreselectionHandlers = ({
  form,
  episodeOfCareProgressNoteData,
  patientId,
  countryCode,
  medgateServiceInfo,
  diagnosisCode,
}: UseCareProviderPreselectionHandlersParameters) => {
  const { setValue, getValues } = form
  const { i18n } = useTranslation()
  const patientAddress = useAppointmentOrRegisteredPatientAddress({
    patientId,
    countryCode,
    episodeOfCareProgressNoteData,
  })
  const isBetterDocEvaluationNeeded =
    isBetterDocAvailable(countryCode) && medgateServiceInfo.hasBetterDocConditionPackage

  const getEffectivePointOfCare = (): PointOfCareType | null =>
    getValues(ACTUAL_POINT_OF_CARE_FIELD) || getValues(MEDICAL_POINT_OF_CARE_FIELD) || null

  const fetchBetterDocUsageEvaluation = (
    effectivePointOfCare: PointOfCareType,
    specialtyId: string | null,
  ): Promise<BetterDocUsageEvaluation | undefined> =>
    fetchBetterDocUsageForConsultation(countryCode, {
      patientId,
      diagnosisCode: getRecordedDiagnosisCode(episodeOfCareProgressNoteData, diagnosisCode),
      effectivePointOfCare,
      specialtyId,
    }).catch(() => undefined)

  const preselectCareProviderUnlessBetterDocIsRequiredOrCanStillChange = async ({
    searchType,
    effectivePointOfCare,
    specialtyId,
    isChoiceStillCurrent,
  }: CareProviderPreselectionRequest): Promise<void> => {
    const [careProviderQuickListResponse, betterDocUsageEvaluation] = await Promise.all([
      fetchQuery<useCareProviderQuickListQuery>(
        RelayEnvironment,
        careProviderQuickListQuery,
        createCareProviderQuickListQueryVariables({
          patientId,
          language: i18n.language,
          searchType,
          specialtyId,
          showInsurerNetwork: true,
          patientAddress,
        }),
        { fetchPolicy: "store-or-network" },
      )
        .toPromise()
        .catch(() => undefined),
      isBetterDocEvaluationNeeded ? fetchBetterDocUsageEvaluation(effectivePointOfCare, specialtyId) : undefined,
    ])
    const preselectedCareProviderId = careProviderQuickListResponse?.ch_careProviderQuickList.preselectedCareProviderId
    if (!preselectedCareProviderId) return

    if (isBetterDocEvaluationNeeded) {
      if (!betterDocUsageEvaluation) return
      const { betterDocUsage, missingConsultationDetailsThatCanChangeBetterDocUsage } = betterDocUsageEvaluation
      if (
        isBetterDocRequired(betterDocUsage) ||
        canBetterDocUsageStillChange(missingConsultationDetailsThatCanChangeBetterDocUsage)
      ) {
        return
      }
    }

    if (isChoiceStillCurrent() && isCareProviderPreselectable(getValues)) {
      setValue("careProviderId", preselectedCareProviderId, SHOULD_VALIDATE_AND_DIRTY)
    }
  }

  const preselectCareProviderForPointOfCare = (selectedPointOfCare: PointOfCareType | undefined): void => {
    if (countryCode !== "CH" || selectedPointOfCare !== "GENERAL_PRACTITIONER") return

    preselectCareProviderUnlessBetterDocIsRequiredOrCanStillChange({
      searchType: "GENERAL_PRACTITIONER",
      effectivePointOfCare: "GENERAL_PRACTITIONER",
      specialtyId: null,
      isChoiceStillCurrent: () => getEffectivePointOfCare() === "GENERAL_PRACTITIONER",
    })
  }

  const preselectCareProviderForSpecialty = (specialtyId: string | undefined): void => {
    if (countryCode !== "CH" || !specialtyId) return

    preselectCareProviderUnlessBetterDocIsRequiredOrCanStillChange({
      searchType: "SPECIALIST_WITH_SPECIALTY",
      effectivePointOfCare: "SPECIALIST",
      specialtyId,
      isChoiceStillCurrent: () =>
        getEffectivePointOfCare() === "SPECIALIST" && getValues("specialtyId")?.specialty?.id === specialtyId,
    })
  }

  return { preselectCareProviderForPointOfCare, preselectCareProviderForSpecialty }
}
